<script setup>
import {useVuelidate} from '@vuelidate/core'
import {required, email, minLength, maxLength} from '@vuelidate/validators'
import {reactive} from '../../../node_modules/vue/dist/vue.esm-bundler';
import {ref, onMounted, watch} from "vue";

const props = defineProps({
  formtype: String
})

const terminationReason = [
  {
    "id": 1,
    "title": "Totalschaden, Verlust des Fahrzeugs",
  },
  {
    "id": 2,
    "title": "Tod des Leasingnehmers",
  }
];

const selectedReasonId = ref(0);

const state = reactive({
  emailSent: false,
  alternateSubject: '',
  isPrivacyChecked: false,
  form: {
    name: '',
    birthday: '',
    contractNumber: '',
    autoNumber: '',
    terminationReason: '',
    dateOfLoss: '',
    dateOfDeath: '',
    contactForInquiries: '',
    email: '',
    telefon: '',
  }
});

const terminationLeasingForm = ref(null);

const rules = {
  // isPrivacyChecked: {required, sameAs: sameAs(true), $autoDirty: true},
  form: {
    name: {required, $autoDirty: true},
    birthday: {required, $autoDirty: true},
    contractNumber: {required, $autoDirty: true},
    autoNumber: {required, $autoDirty: true},
    terminationReason: {required, $autoDirty: true},
    dateOfLoss: {required, $autoDirty: true},
    dateOfDeath: {required, $autoDirty: true},
    contactForInquiries: {required, $autoDirty: true},
    email: {required, $autoDirty: true},
    telefon: {required, $autoDirty: true},
  }
};

let v$ = useVuelidate(rules, state);

const submit = async () => {
  const isFormCorrect = await v$.value.$validate();

  const formData = new FormData(terminationLeasingForm.value);
  formData.append('formtype', props.formtype);

  if (isFormCorrect) {
    console.log("SENT");

    fetch('/form-submit', {
      body: formData,
      method: 'POST'
    })
        .then(response => response.json())
        .then(data => {
          state.emailSent = true;
        }).catch(error => {
      state.emailSent = true;
    });

  }
};

watch(() => state.form.terminationReason, (newSelectedReason) => {
  const reason = terminationReason.find((reason) => reason.title === newSelectedReason);

  if (reason) {
    const fieldRequiredRule = { required, $autoDirty: true };

    if (rules.form.hasOwnProperty("dateOfLoss")) {
      delete rules.form.dateOfLoss;
    }

    if (rules.form.hasOwnProperty("dateOfDeath")) {
      delete rules.form.dateOfDeath;
    }

    if (rules.form.hasOwnProperty("contactForInquiries")) {
      delete rules.form.contactForInquiries;
    }

    if (rules.form.hasOwnProperty("email")) {
      delete rules.form.email;
    }

    if (rules.form.hasOwnProperty("telefon")) {
      delete rules.form.telefon;
    }

    if (reason.id === 1) {
      rules.form['dateOfLoss'] = fieldRequiredRule;
    } else if (reason.id === 2) {
      rules.form['dateOfDeath'] = fieldRequiredRule;
      rules.form['contactForInquiries'] = fieldRequiredRule;
      rules.form['email'] = fieldRequiredRule;
      rules.form['telefon'] = fieldRequiredRule;
    }

    v$ = useVuelidate(rules, state);
    selectedReasonId.value = reason.id;
  }
});

</script>

<template>
  <div class="columns" v-if="!state.emailSent">
    <div class="column is-8 text-flow">
      <h2 class="heading-2">Kündigung Ihres privaten Kilometerleasing-Vertrags</h2>
    </div>
  </div>
  <div class="columns" v-if="!state.emailSent">
    <div class="column is-12 text-flow">
      Der Leasingvertrag ist während der vereinbarten Vertragslaufzeit bindend und kann nicht ordentlich gekündigt werden.
      Eine außerordentliche Kündigung ist nur in folgenden Fällen möglich: Totalschaden, Verlust des Fahrzeugs oder Tod
      des Leasingnehmers.
    </div>
  </div>
  <div class="columns" v-if="!state.emailSent">
    <div class="column is-12 text-flow">
      Wenn Sie Ihren privaten Kilometerleasing-Vertrag aus einem der genannten Gründe kündigen möchten, können Sie dies
      online tun. Wählen Sie den entsprechenden Kündigungsgrund aus und geben Sie Ihre Daten ein. Anschließend können Sie
      Ihren Kündigungswunsch elektronisch übermitteln.
    </div>
  </div>
  <div class="columns" v-if="!state.emailSent">
    <div class="column is-12 text-flow">
      Im Falle der berechtigten außerordentlichen Kündigung rechnet die Bank den Leasingvertrag gemäß unserer allgemeinen
      Geschäftsbedingungen Leasing ab und übersendet Ihnen die Schlussabrechnung.
    </div>
  </div>

  <form action="" class="text-flow" ref="terminationLeasingForm" v-if="!state.emailSent">
    <div class="box">
      <div class="columns is-multiline">
        <div class="column is-9">
          <div class="field" :class="{ 'is-active': state.form.name.length > 0 }">
            <label for="form.name" class="label">Name des Leasingnehmers</label>
            <div class="control">
              <input id="form.name" class="input" :class="{ 'is-danger' : v$.form.name.$errors.length > 0 }" type="text" name="name" placeholder="Name des Leasingnehmers"
                     v-model="state.form.name" @blur="v$.form.name.$touch" required>
            </div>
            <p v-if="v$.form.name.$errors.length > 0" class="help is-danger"><i class="fas fa-warning"></i> Bitte überprüfen Sie Ihre Eingabe.</p>
          </div>

          <div class="field" :class="{ 'is-active': state.form.birthday.length > 0 }">
            <label for="form.birthday" class="label">Geburtsdatum des Leasingnehmers</label>
            <div class="control">
              <input id="form.birthday" class="input" :class="{ 'is-danger' : v$.form.birthday.$errors.length > 0 }" type="date" name="birthday" placeholder="Geburtsdatum des Leasingnehmers"
                     v-model="state.form.birthday"  @blur="v$.form.birthday.$touch" required>
            </div>
            <p v-if="v$.form.birthday.$errors.length > 0" class="help is-danger"><i class="fas fa-warning"></i> Bitte überprüfen Sie Ihre Eingabe.</p>
          </div>

          <div class="field" :class="{ 'is-active': state.form.contractNumber.length > 0 }">
            <label for="form.contractNumber" class="label">Vertragsnummer</label>
            <div class="control">
              <input id="form.contractNumber" class="input" :class="{ 'is-danger' : v$.form.contractNumber.$errors.length > 0 }" type="text" name="contractNumber" placeholder="Vertragsnummer"
                     v-model="state.form.contractNumber"  @blur="v$.form.contractNumber.$touch" required>
            </div>
            <p v-if="v$.form.contractNumber.$errors.length > 0" class="help is-danger"><i class="fas fa-warning"></i> Bitte überprüfen Sie Ihre Eingabe.</p>
          </div>

          <div class="field" :class="{ 'is-active': state.form.autoNumber.length > 0 }">
            <label for="form.autoNumber" class="label">Kennzeichen des Fahrzeugs</label>
            <div class="control">
              <input id="form.autoNumber" class="input" :class="{ 'is-danger' : v$.form.autoNumber.$errors.length > 0 }" type="text" name="autoNumber" placeholder="Kennzeichen des Fahrzeugs"
                     v-model="state.form.autoNumber"  @blur="v$.form.autoNumber.$touch" required>
            </div>
            <p v-if="v$.form.autoNumber.$errors.length > 0" class="help is-danger"><i class="fas fa-warning"></i> Bitte überprüfen Sie Ihre Eingabe.</p>
          </div>

          <div class="field" :class="{ 'is-active': state.form.terminationReason.length > 0 }">
            <label for="form.autoNumber" class="label">Kündigungsgrund</label>
            <div class="control">
              <span class="select is-fullwidth">
                <select
                    id="form.terminationReason"
                    name="terminationReason"
                    :class="{ 'is-danger' : v$.form.terminationReason.$errors.length > 0 }"
                    v-model="state.form.terminationReason"
                    @blur="v$.form.terminationReason.$touch"  required>
                  <option value="" selected disabled>Kündigungsgrund wählen</option>
                  <option v-for="reason of terminationReason" :value="reason.title">{{ reason.title }}</option>
                </select>
              </span>
            </div>
            <p v-if="v$.form.terminationReason.$errors.length > 0" class="help is-danger"><i class="fas fa-warning"></i> Bitte überprüfen Sie Ihre Eingabe.</p>
          </div>

          <div v-if="selectedReasonId === 1" class="field" :class="{ 'is-active': state.form.dateOfLoss.length > 0 }">
            <label for="form.dateOfLoss" class="label">Datum des Totalschadens bzw. Verlust des Fahrzeugs</label>
            <div class="control">
              <input id="form.dateOfLoss" class="input" :class="{ 'is-danger' : v$.form.dateOfLoss.$errors.length > 0 }" type="date" name="dateOfLoss" placeholder="Datum des Totalschadens bzw. Verlust des Fahrzeugs"
                     v-model="state.form.dateOfLoss"  @blur="v$.form.dateOfLoss.$touch" required>
            </div>
            <p v-if="v$.form.dateOfLoss.$errors.length > 0" class="help is-danger"><i class="fas fa-warning"></i> Bitte überprüfen Sie Ihre Eingabe.</p>
          </div>

          <div v-if="selectedReasonId === 2" class="field" :class="{ 'is-active': state.form.dateOfDeath.length > 0 }">
            <label for="form.dateOfDeath" class="label">Sterbedatum</label>
            <div class="control">
              <input id="form.dateOfDeath" class="input" :class="{ 'is-danger' : v$.form.dateOfDeath.$errors.length > 0 }" type="date" name="dateOfDeath" placeholder="Sterbedatum"
                     v-model="state.form.dateOfDeath"  @blur="v$.form.dateOfDeath.$touch" required>
            </div>
            <p v-if="v$.form.dateOfDeath.$errors.length > 0" class="help is-danger"><i class="fas fa-warning"></i> Bitte überprüfen Sie Ihre Eingabe.</p>
          </div>

          <div v-if="selectedReasonId === 2" class="field" :class="{ 'is-active': state.form.contactForInquiries.length > 0 }">
            <label for="form.contactForInquiries" class="label">Ansprechpartner für Rückfragen</label>
            <div class="control">
              <input id="form.contactForInquiries" class="input" :class="{ 'is-danger' : v$.form.contactForInquiries.$errors.length > 0 }" type="text" name="contactForInquiries" placeholder="Ansprechpartner für Rückfragen"
                     v-model="state.form.contactForInquiries"  @blur="v$.form.contactForInquiries.$touch" required>
            </div>
            <p v-if="v$.form.contactForInquiries.$errors.length > 0" class="help is-danger"><i class="fas fa-warning"></i> Bitte überprüfen Sie Ihre Eingabe.</p>
          </div>

          <div v-if="selectedReasonId === 2" class="field" :class="{ 'is-active': state.form.email.length > 0 }">
            <label for="form.email" class="label">E-Mail-Adresse</label>
            <div class="control">
              <input id="form.email" class="input" :class="{ 'is-danger' : v$.form.email.$errors.length > 0 }" type="email" name="email" placeholder="E-Mail-Adresse"
                     v-model="state.form.email"  @blur="v$.form.email.$touch" required>
            </div>
            <p v-if="v$.form.email.$errors.length > 0" class="help is-danger"><i class="fas fa-warning"></i> Bitte überprüfen Sie Ihre Eingabe.</p>
          </div>

          <div v-if="selectedReasonId === 2" class="field" :class="{ 'is-active': state.form.telefon.length > 0 }">
            <label for="form.telefon" class="label">Telefonnummer</label>
            <div class="control">
              <input id="form.telefon" class="input" :class="{ 'is-danger' : v$.form.telefon.$errors.length > 0 }" type="tel" name="telefon" placeholder="Telefonnummer"
                     v-model="state.form.telefon"  @blur="v$.form.telefon.$touch" required>
            </div>
            <p v-if="v$.form.telefon.$errors.length > 0" class="help is-danger"><i class="fas fa-warning"></i> Bitte überprüfen Sie Ihre Eingabe.</p>
          </div>

          <div class="buttons is-left mt-6">
            <a @click.prevent="submit" class="button is-primary">Absenden</a>
          </div>
        </div>
      </div>
    </div>
  </form>

  <div v-else>
    <div class="columns">
      <div class="column text-flow">
        <h2 class="heading-2">Vielen Dank</h2>
        <p>Ihr Kündigungswunsch wurde erfolgreich an uns übermittelt. Wir werden uns kurzfristig mit Ihnen in Verbindung
          setzen.</p>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>